import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Close from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import MediaLibrary from './mediaLibrary/MediaLibrary';
import { IconButton } from '@material-ui/core';
import useFetchData from './hooks/useFetchData';
import './SearchFilterContent.scss';
import DebouncedInput from './DebouncedInput';
import AddFilter from './AddFilter';
import UploadedFilter, { getQueryForOption, UPLOADED_FILTER_OPTIONS } from './UploadedFilter';
import TagsFilter from './TagsFilter';
import { UploadedFilter as UploadedFilterType, TagsFilter as TagsFilterType } from './types';
import LoadingOverlay from 'react-loading-overlay';
import ScaleLoader from 'react-spinners/ScaleLoader';

(LoadingOverlay as any).propTypes = undefined;
interface SearchFilterModalProps {
	item: any;
	isOpenDefault?: boolean;
	hiddenStockLibrary?: boolean;
	hiddenInteractLibrary?: boolean;
	handleReturnedMedia: (_media: object) => void;
}

export enum Option {
    _UPLOADED = 'uploaded',
    _TAGS = 'tags',
};

const DEFAULT_UPLOADED_FILTER = {
	option: UPLOADED_FILTER_OPTIONS._DATE,
	unit: "days",
	value: "1",
};

export const options = Object.values(Option) as Option[];

const SearchFilterModal = forwardRef((props: SearchFilterModalProps, ref): any => {
	const { t: translator } = useTranslation();

	const { item = {}, isOpenDefault = false, handleReturnedMedia } = props;

	const [isOpen, setVisibleStatus] = useState(isOpenDefault);
	const [filterName, setFilterName] = useState('');
	const [isAllowToSubmit, setAllowToSubmit] = useState(false);

	const defaultFilters = (): Record<Option, boolean> =>
		(Object.values(Option) as Option[]).reduce((acc, opt) => {
			acc[opt] = false;
			return acc;
		}, {} as Record<Option, boolean>);

	const [selectedFilters, setSelectedFilters] = useState<Record<Option, boolean>>(defaultFilters);
	const [selectedFiltersForAddFilter, setSelectedFiltersForAddFilter] = useState<Record<Option, boolean>>(defaultFilters);
	const [uploadedFilter, setUploadedFilter] = useState<UploadedFilterType | null>(null);
	const [tagsFilter, setTagsFilter] = useState<TagsFilterType | null>(null);

	const {
		medias,
		mediaTags,
		totalCount,
		page,
		rowsPerPage,
		isFetchingData,
		setTimeQuery,
		setTagsQuery,
		setPaginationInfo,
	} = useFetchData(isOpen);

	useImperativeHandle(ref, () => {
		return {
			showMediaLibrary: (isOpen: boolean) => {
				setVisibleStatus(isOpen);
			},
		};
	});

	const closeMediaLibrary = useCallback(() => {
		setVisibleStatus(false);
	}, []);

	const handleActionButtonClick = () => {
		handleReturnedMedia({
			name: filterName,
			selectedFilters,
			uploadedFilter,
			tagsFilter,
		});
		setVisibleStatus(false);
	};

	const handleUploadedFilterChange = useCallback((value: any) => {
		const query = getQueryForOption(value);
		setTimeQuery(query);
		setUploadedFilter(value);
	}, []);

	const handleTagsFilterChange = useCallback((data: TagsFilterType) => {
		setTagsQuery(data.query);
		setTagsFilter(data);
	}, []);

	const toggleFilterButtonHandler = (filters: Record<Option, boolean>) => {
		setSelectedFilters(filters);
		// apply changes if a filter is removed from checkbox dropdown
		if (!filters[Option._UPLOADED]) {
			setTimeQuery('');
			setUploadedFilter(null);
		}
		if (!filters[Option._TAGS]) {
			setTagsQuery('');
			setTagsFilter(null);
		}
	};

	const removeFilterHanlder = useCallback((filter: string) => {
		setSelectedFilters(prev => {
			const newFilters = {
				...prev,
				[filter]: false
			};
			// update the Add filter menu if a filter is removed
			setSelectedFiltersForAddFilter(newFilters);
			return newFilters
		});
	}, []);

	// initialize filters
	useEffect(() => {
		setFilterName(item.name);
		setTagsFilter(item.tagsFilter);
		setUploadedFilter(item.uploadedFilter);
		setTimeQuery(getQueryForOption(item.uploadedFilter));
		setTagsQuery(item.query);
		item.selectedFilters && setSelectedFilters(item.selectedFilters);
		item.selectedFilters && setSelectedFiltersForAddFilter(item.selectedFilters);
	}, [isOpen]);

	useEffect(() => {
		if (selectedFilters[Option._UPLOADED] && !uploadedFilter) {
			setUploadedFilter(DEFAULT_UPLOADED_FILTER);
			setTimeQuery(getQueryForOption(DEFAULT_UPLOADED_FILTER));
		}
	}, [selectedFilters]);

	useEffect(() => {
		if (filterName) {
			setAllowToSubmit(true);
		} else {
			setAllowToSubmit(false);
		}
	}, [filterName]);

	// apply changes if a filter is removed from the filter x icon
	useEffect(() => {
		if (!selectedFiltersForAddFilter[Option._UPLOADED]) {
			setTimeQuery('');
			setUploadedFilter(null);
		}
		if (!selectedFiltersForAddFilter[Option._TAGS]) {
			setTagsQuery('');
			setTagsFilter(null);
		}
	}, [selectedFiltersForAddFilter]);

	return (
		<Dialog className="media-library" open={isOpen} fullWidth={true} maxWidth={'md'}>
			<DialogTitle className="dialogTitle" disableTypography>
				<Typography variant="h6">
					{translator('MODAL_CREATE_SEARCH_FILTER_TITLE')}
				</Typography>
				<IconButton onClick={closeMediaLibrary}>
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<Box className="dialog-menu">
					{selectedFilters[Option._UPLOADED] &&
						<UploadedFilter
							data={uploadedFilter}
							onChange={(handleUploadedFilterChange)}
							removeFilter={removeFilterHanlder}
						/>
					}
					{selectedFilters[Option._TAGS] &&
						<TagsFilter
							data={tagsFilter}
							mediaTags={mediaTags}
							onChange={handleTagsFilterChange}
							removeFilter={removeFilterHanlder}
						/>
					}
					<AddFilter
						onChange={toggleFilterButtonHandler}
						selectedFilters={selectedFiltersForAddFilter}
					/>
				</Box>
				<Box className="dialog-content">
					{isFetchingData ?
						<LoadingOverlay
							className="dialog-loader"
							active={true}
							spinner={<ScaleLoader color={'#000'} />}
							styles={{
								overlay: (base) => ({
									...base,
									position: 'absolute',
									background: 'transparent',
									top: 0,
									left: 0,
								}),
							}}
						/> :
						<MediaLibrary
							medias={medias}
							totalCount={totalCount}
							page={page}
							rowsPerPage={rowsPerPage}
							onPageChange={(_, number) => {
								setPaginationInfo({
									page: number,
									rowsPerPage,
								});
							}}
							onRowPerPageChange={(event) => {
								setPaginationInfo({ page, rowsPerPage: parseInt(event.target.value, 10) });
							}}
						/>
					}
				</Box>
			</DialogContent>
			<Box className="dialog-input-name">
				<DebouncedInput
					id="search-filter-name"
					label="Name search filter"
					placeholder="Enter a name"
					value={filterName}
					onChange={setFilterName}
				/>
			</Box>
			<DialogActions className="dialog-actions">
				<Button
					variant="contained"
					color="primary"
					className="no-shadow"
					onClick={handleActionButtonClick}
					disabled={!isAllowToSubmit}
				>
					{ item.name ?
						translator('COMMON_UPDATE') :
						translator('COMMON_CREATE')
					}
				</Button>
			</DialogActions>
		</Dialog>
	);
});

export default SearchFilterModal;
